// Global array to track all delivery frequency instances
window.deliveryFrequencyInstances = window.deliveryFrequencyInstances || [];

// Make the class available globally
window.DeliveryFrequencySelector = class DeliveryFrequencySelector {
  constructor(selector) {
    this.selector = selector;
    if (!this.selector) return;
    
    this.panel = this.selector.querySelector('.delivery-date-panel');
    this.overlay = this.selector.querySelector('.delivery-panel-overlay');
    this.dateOptions = this.selector.querySelector('#dateOptions');
    this.totalAmountElement = this.selector.querySelector('#deliveryTotalAmount');
    this.freeShippingLabel = this.selector.querySelector('[data-free-shipping]');
    this.paidShippingLabel = this.selector.querySelector('[data-paid-shipping]');
    
    this.selectedDate = null;
    this.selectedFrequency = 'onetime';
    this.currentStep = 1;
    this.cartTotal = 0;
    
    // Determine if this is a drawer or page instance©DISC
    this.isDrawer = this.selector.id === 'DrawerDeliveryFrequencySelector' || 
                    this.selector.closest('.drawer') !== null ||
                    this.selector.closest('[data-js-site-cart-sidebar]') !== null;
    this.instanceType = this.isDrawer ? 'drawer' : 'page';
    
    this.isModal = this.selector.classList.contains('delivery-frequency-modal');
    
    this.init();
    
    // Add this instance to the global array
    window.deliveryFrequencyInstances.push(this);
    
    // Listen for cart updates
    document.addEventListener('cart-updated', this.handleCartUpdate.bind(this));
    
    // Reset delivery date when page is unloaded (user navigates away)
    window.addEventListener('beforeunload', () => {
      if (!this.selectedFrequency) {
        // Only reset if the user hasn't completed the selection process
        localStorage.removeItem('tempSelectedDate');
        localStorage.removeItem('selectedDeliveryDate');
      }
    });
  }
  
  init() {
    console.log(`Initializing DeliveryFrequencySelector (${this.instanceType})`);
    
    // Check if customer is signed in and has a subscription
    this.checkCustomerStatus();
    
    // Generate delivery dates
    this.generateDateOptions();
    
    // Load saved selections from localStorage
    this.loadSavedSelections();
    
    // Initialize event listeners
    this.initEventListeners();
    
    // Compare cart with last order if customer has subscription
    this.compareCartWithLastOrder();
    
    // Display saved selections in cart (but don't select them in the UI)
    this.displaySavedSelectionsInCart();
    
    // Listen for the show-delivery-selector event
    document.addEventListener('show-delivery-selector', (event) => {
      // Only respond if this instance is the target or no target is specified
      const targetType = event.detail?.target;
      if (!targetType || targetType === this.instanceType) {
        this.showDeliverySelector(event.detail?.isDrawer);
      } else {
        console.log(`Ignoring event - targeted at ${targetType}, this is ${this.instanceType}`);
      }
    });
    
    // Listen for cart updates to reload delivery dates
    document.addEventListener('cart:delivery-dates-reload', this.handleDeliveryDatesReload.bind(this));
    
    // Store the current cart state for comparison
    this.cartState = {
      hasOutOfStock: this.checkCartForOutOfStockItems(),
      itemCount: document.querySelectorAll('.cart-item').length
    };
    
    // Update confirm button text
    this.updateConfirmButtonText();
    
    console.log("DeliveryFrequencySelector initialization complete");
  }
  
  // Check if customer is signed in and has a subscription
  checkCustomerStatus() {
    // Check if customer is signed in by looking for the lastOrderVariants input
    const lastOrderInput = document.getElementById('lastOrderVariants');
    
    // Check if customer has a subscription by looking for customer subscription data
    const customerSubscriptionElement = document.querySelector('[data-customer-subscription]');
    
    // Get the cart comparison status element
    const cartComparisonStatus = this.selector.querySelector('.cart-comparison-status');
    
    if (cartComparisonStatus) {
      // Hide the cart comparison status if customer is not signed in or doesn't have a subscription
      if (!lastOrderInput || !customerSubscriptionElement || !customerSubscriptionElement.getAttribute('data-customer-subscription')) {
        cartComparisonStatus.style.display = 'none';
      } else {
        cartComparisonStatus.style.display = 'block';
      }
    }
  }
  
  loadSavedSelections() {
    const savedDate = localStorage.getItem('selectedDeliveryDate');
    const savedFrequency = localStorage.getItem('selectedDeliveryFrequency');
    
    if (savedDate) {
      this.selectedDate = new Date(savedDate);
    }
    
    // Always force onetime frequency for now as we hid the selection step
    this.selectedFrequency = 'onetime';
    
    // Set current step based on saved selections
    if (this.selectedDate) {
      // Stay on step 1 but with date selected
      this.currentStep = 1;
    }
    
    console.log('Loaded saved selections:', { date: this.selectedDate, frequency: this.selectedFrequency });
  }
  
  generateDateOptions() {
    console.log("Starting generateDateOptions");
    const dateOptions = document.getElementById('dateOptions');
    if (!dateOptions) {
      console.log("dateOptions element not found");
      return;
    }
    
    // Log blackout dates for debugging
    const blackoutDates = window.deliverySettings?.blackoutDates || [];
    console.log("Blackout dates from settings:", blackoutDates);
    
    // Log one-time dates for debugging
    const oneTimeDates = window.deliverySettings?.oneTimeDates || [];
    console.log("One-time dates from settings:", oneTimeDates);
    
    // Check if cart has items with one-time delivery tag
    const hasOneTimeItems = this.hasOneTimeDeliveryTag();
    console.log("Cart has one-time delivery items:", hasOneTimeItems);
    
    // Log one-time dates tags for debugging
    const oneTimeDatesTags = window.deliverySettings?.oneTimeDatesTags || [];
    console.log("One-time dates tags from settings:", oneTimeDatesTags);
    
    // Clear existing options
    dateOptions.innerHTML = '';
    
    // Get current date and time in Israel time
    const now = new Date();
    console.log("Current date and time:", now);
    
    // Get delivery days settings
    const deliveryDays = window.deliverySettings?.deliveryDays || {
      sunday: true,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: true
    };
    
    // Check if we have out-of-stock items
    const hasOutOfStockItems = this.checkCartForOutOfStockItems();
    console.log("Has out-of-stock items:", hasOutOfStockItems);
    
    // Get the next available delivery date for out-of-stock items
    let nextAvailableDate = null;
    if (hasOutOfStockItems) {
      nextAvailableDate = this.getNextAvailableDeliveryDate();
      console.log("Next available date for out-of-stock items:", nextAvailableDate);
    }
    
    console.log("Delivery days settings:", deliveryDays);
    
    // Find the closest delivery day
    let closestDeliveryDay = null;
    let daysUntilClosestDelivery = 30;
    
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(checkDate.getDate() + i);
      const dayName = this.getDayName(checkDate).toLowerCase();
      
      // Check if this is a one-time delivery date or a regular delivery day
      if (this.isOneTimeDate(checkDate) || (deliveryDays[dayName] && !this.isBlackoutDate(checkDate))) {
        // Check if this day is past the cutoff time
        if (i === 0) {
          const cutoffHour = this.getCutoffHour(dayName);
          const currentHour = now.getHours();
          
          if (currentHour >= cutoffHour) {
            // Today is past cutoff, skip it
            continue;
          }
        }
        
        
        closestDeliveryDay = checkDate;
        daysUntilClosestDelivery = i;
        break;
      }
    }
    
 console.log("Closest delivery day:", closestDeliveryDay);
 console.log("Days until closest delivery:", daysUntilClosestDelivery);
    
    // Generate date options for the next 14 days (limit to 4 for the 2x2 grid)
    let deliveryDatesFound = 0;
    const maxDeliveryDates = 4;
    
    for (let i = 0; i < 32; i++) { // Look ahead up to 30 days to find 6 delivery dates
      if (deliveryDatesFound >= maxDeliveryDates) {
        break; // Stop once we've found enough delivery dates
      }
      
      const date = new Date(now);
      date.setDate(date.getDate() + i);
 console.log(`Checking date ${i}:`, date);
      
      // Get day name
      const dayName = this.getDayName(date).toLowerCase();
    //  console.log(`Day name: ${dayName}`);
      
      // Check if this is a one-time delivery date
      const isOneTimeDelivery = this.isOneTimeDate(date);
      
      // If we have items with one-time delivery tag, only show one-time delivery dates
      if (hasOneTimeItems && !isOneTimeDelivery) {
        //console.log(`Skipping ${date} - not a one-time delivery date and cart has one-time items`);
        continue;
      }
      
      // Skip days that don't have delivery and aren't one-time dates
      if (!isOneTimeDelivery && !deliveryDays[dayName]) {
     //   console.log(`Skipping ${dayName} - not a delivery day`);
        continue;
      }
      
      // Determine if this date should be disabled
      let isDisabled = false;
      let disableReason = "";
      let isSpecialDate = isOneTimeDelivery;

      {
        // Get delivery time for this day (e.g., "10:00 AM")
        const deliveryTimeStr = window.deliverySettings?.deliveryTimes?.[dayName] || "10:00 AM";
        let [time, ampm] = deliveryTimeStr.split(' ');
        let [deliveryHour, deliveryMinute] = time.split(':').map(Number);
        if (ampm && ampm.toUpperCase() === "PM" && deliveryHour < 12) deliveryHour += 12;
        if (ampm && ampm.toUpperCase() === "AM" && deliveryHour === 12) deliveryHour = 0;
      
        // Get cutoff in hours for this day
        const cutoffHours = window.deliverySettings?.cutoffHours?.[dayName] || 1;
      
        // Build the delivery datetime for this date
        const deliveryDateTime = new Date(date);
        deliveryDateTime.setHours(deliveryHour, deliveryMinute, 0, 0);
      
        // Calculate the cutoff datetime
        const cutoffDateTime = new Date(deliveryDateTime.getTime() - cutoffHours * 60 * 60 * 1000);
      
        // Log current time, delivery time, cutoff time, and hours left
        const nowTime = new Date();
        console.log(`[Cutoff Check] Now: ${nowTime}, Delivery: ${deliveryDateTime}, Cutoff: ${cutoffDateTime}`);
        const hoursLeft = (cutoffDateTime - nowTime) / (1000 * 60 * 60);
        console.log(`[Cutoff Check] Now: ${nowTime}, Delivery: ${deliveryDateTime}, Cutoff: ${cutoffDateTime}, Hours left: ${hoursLeft}`);
      
        if (nowTime >= cutoffDateTime) {
          console.log(`Disabling ${date.toISOString().split('T')[0]} - past cutoff time`);
          isDisabled = true;
          disableReason = "past cutoff";
        }
      }
      
      // Check if this date is past the cutoff time for today
      if (i === 0) {
        const cutoffHour = this.getCutoffHour(dayName);
        const currentHour = now.getHours();
        
      console.log(`Today's cutoff hour: ${cutoffHour}, current hour: ${currentHour}`);
        
        if (currentHour >= cutoffHour) {
          console.log(`Disabling today - past cutoff time`);
          isDisabled = true;
          disableReason = "past cutoff";
        }
      }
      
      // Check if this date is a blackout date, but only if it's not a one-time delivery date
      // Note: We're ensuring one-time delivery dates take priority over blackout dates
      if (!isDisabled && !isOneTimeDelivery && this.isBlackoutDate(date)) {
        console.log(`Disabling ${date.toISOString().split('T')[0]} - blackout date`);
        isDisabled = true;
        disableReason = "blackout date";
      }
      
      // Special case for dates that are both blackout and one-time delivery
      if (isOneTimeDelivery && this.isBlackoutDate(date)) {
        console.log(`Date ${date.toISOString().split('T')[0]} is both a blackout date and one-time date - prioritizing one-time delivery`);
        // Ensure it's not disabled
        isDisabled = false;
        disableReason = "";
      }
      
      // For out-of-stock items, implement the special logic:
      // If there's an out-of-stock item, we need to skip the closest delivery day
      // and show the next one if the closest is <= 6 days away
      if (hasOutOfStockItems && !isDisabled) {
        if (closestDeliveryDay && daysUntilClosestDelivery <= 6) {
          // If this is the closest delivery day, disable it
          if (this.isSameDay(date, closestDeliveryDay)) {
            console.log(`Disabling ${date} - it's the closest delivery day and we have out-of-stock items`);
            isDisabled = true;
            disableReason = "out of stock - skipping closest";
          }
        }
        
        // Also check against the next available date from the badge
        if (nextAvailableDate) {
          const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const nextAvailableDateWithoutTime = new Date(
            nextAvailableDate.getFullYear(), 
            nextAvailableDate.getMonth(), 
            nextAvailableDate.getDate()
          );
          
          if (dateWithoutTime < nextAvailableDateWithoutTime) {
            console.log(`Disabling ${date} - before next available date ${nextAvailableDate}`);
            isDisabled = true;
            disableReason = "before next available date";
          }
        }
      }
      
      // Skip adding more date options if we've already found enough delivery dates
      if (deliveryDatesFound >= maxDeliveryDates && !isDisabled) {
        console.log(`Skipping additional date ${date.toISOString().split('T')[0]} - already have enough dates`);
        continue;
      }
      
      // Create the date card (new design)
      const dateCard = document.createElement('div');
      dateCard.className = `date-card-new${isDisabled ? ' disabled' : ''}`;
      
      // Add specific class for blackout dates for additional styling
      if (disableReason === "blackout date") {
        dateCard.classList.add('blackout-date');
      }
      
      // Add specific class for one-time dates
      if (isSpecialDate) {
        dateCard.classList.add('one-time-date');
      }
      
      dateCard.dataset.date = date.toISOString();
      if (disableReason) {
        dateCard.dataset.disableReason = disableReason;
      }
      if (isSpecialDate) {
        dateCard.dataset.specialDate = 'one-time';
      }
      
      // Format the date display
      const dayOfWeek = this.getDayNameHebrew(date);
      const formattedDate = this.formatDate(date);
      
      // Check if this is a Sunday (day 0)
      const isSunday = date.getDay() === 0;
      
      // Check if this is the first/closest date
      const isClosest = deliveryDatesFound === 0 && !isDisabled;
      
      // Create the HTML for the date card
      let dateCardHTML = `
        <div class="date-day">${dayOfWeek}</div>
        <div class="date-number">${formattedDate}</div>
      `;
      
      // Add "Closest" badge for the first available date
      if (isClosest) {
        dateCardHTML += `<div class="date-badge">הכי קרוב</div>`;
      }
      
      dateCard.innerHTML = dateCardHTML;
      
      // Add click handler if not disabled
      if (!isDisabled) {
        dateCard.addEventListener('click', () => this.selectDate(date));
      } else {
        console.log(`Date ${formattedDate} is disabled: ${disableReason}`);
      }
      
      dateOptions.appendChild(dateCard);
      console.log(`Added date card: ${formattedDate}, disabled: ${isDisabled}`);
      
      // Only count enabled delivery dates
      if (!isDisabled) {
        deliveryDatesFound++;
        
        // If we've reached our desired number of delivery dates, break out of the loop
        if (deliveryDatesFound >= maxDeliveryDates) {
          console.log(`Found ${maxDeliveryDates} delivery dates, stopping date generation`);
          break;
        }
      }
    }
    
    // Log all the date options that were added
    console.log(`Total date options added: ${dateOptions.children.length}`);
    console.log(`Enabled delivery dates found: ${deliveryDatesFound}`);
    
    // If we're showing one-time delivery dates only, add a message
    if (hasOneTimeItems) {
      // Check if message already exists to avoid duplicates
      const existingMessage = document.querySelector('.one-time-delivery-message');
      if (!existingMessage) {
        // Create a message container
        const messageContainer = document.createElement('div');
        messageContainer.className = 'one-time-delivery-message';
        messageContainer.innerHTML = '<p>מוצג זמן אספקה מיוחד עבור המוצרים שבעגלה</p>'; // "Special delivery time shown for cart items"
        messageContainer.style.textAlign = 'center';
        messageContainer.style.marginTop = '10px';
        messageContainer.style.padding = '8px';
        messageContainer.style.backgroundColor = '#e6f7ff';
        messageContainer.style.borderRadius = '6px';
        messageContainer.style.fontSize = '14px';
        messageContainer.style.color = '#0066cc';
        
        // Insert after the date options
        const dateOptionsContainer = dateOptions.parentNode;
        if (dateOptionsContainer) {
          dateOptionsContainer.insertBefore(messageContainer, dateOptions.nextSibling);
        }
      }
    } else {
      // Remove the message if items no longer have the one-time tag
      const existingMessage = document.querySelector('.one-time-delivery-message');
      if (existingMessage) {
        existingMessage.remove();
      }
    }
  }
  
  getNextDayOfWeek(date, dayOfWeek, weekOffset = 0) {
    const resultDate = new Date(date.getTime());
    resultDate.setDate(date.getDate() + (7 + dayOfWeek - date.getDay()) % 7 + (weekOffset * 7));
    return resultDate;
  }
  
  isSameDay(date1, date2) {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  }
  
  getDayName(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = days[date.getDay()];
    //console.log(`Getting day name for ${date}: ${dayName}`);
    return dayName;
  }
  
  formatDate(date) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  }
  
  setCartTotal() {
    // Get cart total from data attribute or use a default value
    const cartTotalElement = document.querySelector('[data-cart-total]');
    if (cartTotalElement) {
      // Get the cart items subtotal (without shipping)
      const cartItemsSubtotal = document.querySelector('[data-cart-items-subtotal]');
      if (cartItemsSubtotal) {
        const subtotalText = cartItemsSubtotal.textContent.trim().replace('₪', '');
        this.cartTotal = subtotalText;
      } else {
        // Fallback to cart total if subtotal not found
      const cartTotalText = cartTotalElement.textContent.trim().replace('₪', '');
        this.cartTotal = cartTotalText;
      }
    }
    
    if (this.totalAmountElement) {
      console.log("cartTotal", this.cartTotal);
      this.totalAmountElement.textContent = this.cartTotal;
    }
  }
  
  initEventListeners() {
    // Close panel button (support both old and new classes)
    const closeButtons = this.selector.querySelectorAll('[data-action="close"], .close-panel-new');
    closeButtons.forEach(closeButton => {
      closeButton.addEventListener('click', () => this.closePanel());
    });
    
    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closePanel());
    }
    
    // Next step buttons
    const nextStepButtons = this.selector.querySelectorAll('[data-action="next-step"]');
    if (nextStepButtons) {
      nextStepButtons.forEach(button => {
        button.addEventListener('click', () => this.goToNextStep());
      });
    }
    
    // Previous step buttons
    const prevStepButtons = this.selector.querySelectorAll('[data-action="prev-step"]');
    if (prevStepButtons) {
      prevStepButtons.forEach(button => {
        button.addEventListener('click', () => this.goToPreviousStep());
      });
    }
    
    // Clickable steps (only steps 1 and 2)
    const stepsNav = this.selector.querySelectorAll('[data-action="goto-step"]');
    if (stepsNav) {
      stepsNav.forEach(step => {
        step.addEventListener('click', () => {
          const targetStep = parseInt(step.getAttribute('data-target-step'));
          this.goToStep(targetStep);
        });
      });
    }
    
    // Frequency options
    const frequencyOptions = this.selector.querySelectorAll('.frequency-option');
    frequencyOptions.forEach(option => {
      option.addEventListener('click', this.handleFrequencyClick.bind(this));
      
      // Set discount information for each frequency option
      const frequency = option.dataset.frequency;
      const discountElement = option.querySelector('.discount-info');
      
      if (discountElement && frequency) {
        discountElement.textContent = this.getDiscountInfo(frequency);
      }
    });
    
    // Update the confirm button behavior (support both old and new classes)
    const confirmButtons = this.selector.querySelectorAll('[data-action="confirm"], .checkout-btn-new');
    if (confirmButtons) {
      confirmButtons.forEach(confirmButton => {
        confirmButton.addEventListener('click', (event) => {
          // Always prevent default form submission
          event.preventDefault();
          
          console.log('Checkout button clicked', { selectedDate: this.selectedDate, selectedFrequency: this.selectedFrequency });
          
          // Try to get selected date from UI if not set in memory
          if (!this.selectedDate) {
            const selectedCard = this.selector.querySelector('.date-card-new.selected, .date-option.selected');
            if (selectedCard) {
              const dateStr = selectedCard.dataset.date;
              if (dateStr) {
                this.selectedDate = new Date(dateStr);
                console.log('Retrieved date from UI:', this.selectedDate);
              }
            }
          }
          
          // Validate date selection before confirming
          if (!this.selectedDate) {
            this.showValidationError('date');
            return false;
          }
          
          // Direct confirmation
          this.confirmSelection();
        });
      });
    }
    
    // Listen for cart close events
    document.addEventListener('cart:close', () => {
      // Reset delivery date when cart is closed
      this.selectedDate = null;
      localStorage.removeItem('tempSelectedDate');
      localStorage.removeItem('selectedDeliveryDate');
    });
    
    // Step navigation
    const stepElements = this.selector.querySelectorAll('.step');
    stepElements.forEach((step, index) => {
      step.addEventListener('click', () => {
        const stepNumber = index + 1;
        
        // Only allow clicking on step 1 if we're on step 2
        if (stepNumber === 1 && this.currentStep === 2) {
          console.log('Step 1 clicked from step 2');
          // Clear validation messages before going back
          this.clearValidationMessages();
          
          // Force to step 1
          this.currentStep = 1;
          
          // Update UI
          this.updateStepUI();
          this.updateConfirmButtonText();
          this.updateButtonLayout();
          
          // Extra cleanup
          setTimeout(() => {
            this.clearValidationMessages();
          }, 50);
        }
      });
    });
    
    // Hide the back button in the step actions
    const stepActionsBackBtn = this.selector.querySelector('.step-actions .back-step-btn');
    if (stepActionsBackBtn) {
      stepActionsBackBtn.style.display = 'none';
    }
  }
  
  selectDate(date) {
    console.log("Selecting date:", date);
    this.selectedDate = date;
    
    // Store in localStorage
    localStorage.setItem('selectedDeliveryDate', date.toISOString());
    localStorage.setItem('tempSelectedDate', date.toISOString());
    
    // Update UI - support both old and new class names
    const dateCards = this.selector.querySelectorAll('.date-card-new, .date-option');
    dateCards.forEach(card => {
      card.classList.remove('selected');
      
      // Safely check if the date matches
      try {
        const dayElement = card.querySelector('.date-day');
        const dateElement = card.querySelector('.date-number');
        
        if (dayElement && dateElement) {
          const dayName = dayElement.textContent;
          const dateText = dateElement.textContent;
          const cardDate = this.parseDateFromText(dayName, dateText);
      
          if (cardDate && this.isSameDay(cardDate, date)) {
            card.classList.add('selected');
          }
        }
      } catch (e) {
        console.error("Error comparing dates:", e);
      }
    });
    
    // Enable next step button
    const nextStepBtn = this.selector.querySelector('[data-action="next-step"]');
    if (nextStepBtn) {
      nextStepBtn.disabled = false;
    }

    // Enable confirm button (new class)
    const confirmBtns = this.selector.querySelectorAll('[data-action="confirm"], .checkout-btn-new');
    if (confirmBtns) {
      confirmBtns.forEach(btn => {
        btn.disabled = false;
        btn.classList.remove('disabled');
      });
    }
    
    // Mark step as completed (support both old and new stepper)
    const dateStep = this.selector.querySelector('[data-step="1"], .stepper-step[data-step="1"]');
    if (dateStep) {
      dateStep.classList.add('completed');
    }
    
    // Update connector
    const connector = this.selector.querySelector('.step-connector, .stepper-connector');
    if (connector) {
      connector.classList.add('active');
    }
  }
  
  parseDateFromText(dayName, dateText) {
    try {
      // Parse the date from the text (format is now "dd/mm")
      const parts = dateText.trim().split('/');
      if (parts.length !== 2) return null;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
      
      if (isNaN(day) || isNaN(month) || month < 0 || month > 11) {
        console.error("Invalid date parts:", parts);
        return null;
      }
      
      const year = new Date().getFullYear();
      return new Date(year, month, day);
    } catch (e) {
      console.error("Error parsing date:", e);
      return null;
    }
  }
  
  handleFrequencyClick(event) {
    const frequencyOption = event.currentTarget;
    const frequency = frequencyOption.dataset.frequency;
    
    if (!frequency) return;
    
    console.log(`Selected frequency: ${frequency}`);
    
    // Update the selected frequency
    this.selectedFrequency = frequency;
    
    // Store the selected frequency in localStorage temporarily
    localStorage.setItem('tempSelectedFrequency', frequency);
    
    // Update the UI
    const frequencyOptions = this.selector.querySelectorAll('.frequency-option');
    frequencyOptions.forEach(option => {
      option.classList.remove('selected');
    });
    
    frequencyOption.classList.add('selected');
    
    // Clear any validation messages
    this.clearValidationMessages();
    
    // Enable the confirm button
    const confirmButton = this.selector.querySelector('[data-action="confirm"]');
    if (confirmButton) {
      confirmButton.disabled = false;
    }
    
    // Update shipping labels based on frequency
    this.updateShippingLabels(frequency);
  }
  
  updateShippingLabels(frequency) {
    const freeShippingLabel = this.selector.querySelector('[data-free-shipping]');
    const paidShippingLabel = this.selector.querySelector('[data-paid-shipping]');
    const cartTotal = parseFloat(this.totalAmountElement.textContent) || 0;
    
    if (!freeShippingLabel || !paidShippingLabel) return;
    
    // Calculate discount amount if applicable
    let discountMessage = '';
    
    if (frequency === 'weekly') {
      // 10% discount
      const discountAmount = cartTotal * 0.1;
      discountMessage = ` 🎉 10% הנחה (₪${discountAmount.toFixed(0)}) על ההזמנה הבאה`;
    } else if (frequency === 'biweekly') {
      // 5% discount
      const discountAmount = cartTotal * 0.05;
      discountMessage = ` 🎉 5% הנחה (₪${discountAmount.toFixed(0)}) על ההזמנה הבאה`;
    } else if (frequency === 'monthly') {
      // Surprise gift
      discountMessage = ' 🎉 הפתעה טעימה בהזמנה הבאה';
    } else if (frequency === 'onetime') {
      // Free shipping
      discountMessage = 'יהיה טעים';
    }
    
    // If there's a currently visible label, just fade it out
    if (!freeShippingLabel.classList.contains('hidden')) {
      // Add the fade-out animation
      freeShippingLabel.classList.add('shipping-label-animate-out');
      
      // After the fade-out animation completes, update the content and slide in
      setTimeout(() => {
        // Update the content
        freeShippingLabel.textContent = discountMessage;
        
        // Remove the fade-out animation
        freeShippingLabel.classList.remove('shipping-label-animate-out');
        
        // Force a reflow
        void freeShippingLabel.offsetWidth;
        
        // Add the slide-in animation
        freeShippingLabel.classList.add('shipping-label-animate-in');
        
        // Make sure it's visible
        freeShippingLabel.classList.remove('hidden');
      }, 200); // Match this to the fade-out animation duration
    } else {
      // No visible label, just update and slide in
      // Update the content
      freeShippingLabel.textContent = discountMessage;
      
      // Remove any existing animations
      freeShippingLabel.classList.remove('shipping-label-animate-out');
      freeShippingLabel.classList.remove('shipping-label-animate-in');
      
      // Make sure it's visible but off-screen
      freeShippingLabel.classList.remove('hidden');
      freeShippingLabel.style.transform = 'translateX(-100%)';
      freeShippingLabel.style.opacity = '0';
      
      // Force a reflow
      void freeShippingLabel.offsetWidth;
      
      // Add the slide-in animation
      freeShippingLabel.classList.add('shipping-label-animate-in');
    }
    
    // Hide the paid shipping label
    paidShippingLabel.classList.add('hidden');
  }
  
  goToNextStep() {
    // Always validate date selection before proceeding
    if (this.currentStep === 1 && !this.selectedDate) {
      this.showValidationError('date');
      return; // Don't proceed if no date is selected
    }
    
    if (this.currentStep < 2) {
      this.currentStep++;
      
      // Update the UI
      this.updateStepUI();
      
      // Enable/disable the confirm button based on step
      const confirmButton = this.selector.querySelector('[data-action="confirm"]');
      if (confirmButton) {
        if (this.currentStep === 1) {
          // Always enable on first step
          confirmButton.disabled = false;
          confirmButton.classList.remove('disabled');
        } else if (this.currentStep === 2) {
          // On second step, only enable if frequency is selected
          confirmButton.disabled = !this.selectedFrequency;
          if (!this.selectedFrequency) {
            confirmButton.classList.add('disabled');
          } else {
            confirmButton.classList.remove('disabled');
          }
        }
      }
    }
    
    // Update the confirm button text
    this.updateConfirmButtonText();
    
    // Update the button layout
    this.updateButtonLayout();
  }
  
  goToPreviousStep() {
    console.log('goToPreviousStep called');
    
    if (this.currentStep > 1) {
      // Clear validation messages before changing steps
      this.clearValidationMessages();
      
      // Set the current step
      this.currentStep = 1; // Force to step 1 since we only have 2 steps
      
      // Update the UI
      this.updateStepUI();
      
      // Update the confirm button text
      this.updateConfirmButtonText();
      
      // Update the button layout
      this.updateButtonLayout();
      
      // Make sure validation messages are cleared after UI update
      setTimeout(() => {
        this.clearValidationMessages();
        
        // Also clear any validation styling
        const dateOptions = this.selector.querySelector('.date-options');
        if (dateOptions) {
          dateOptions.classList.remove('validation-error');
        }
        
        const frequencyOptions = this.selector.querySelector('.frequency-options');
        if (frequencyOptions) {
          frequencyOptions.classList.remove('validation-error');
        }
      }, 50);
    }
  }
  
  goToStep(step) {
    // Only allow going to steps 1 and 2
    if (step <= 2 && (step < this.currentStep || (step === 2 && this.selectedDate))) {
      this.currentStep = step;
      this.updateStepUI();
    }
  }
  
  updateStepUI() {
    // Update step indicators
    const steps = this.selector.querySelectorAll('.step');
    steps.forEach(step => {
      const stepNumber = parseInt(step.getAttribute('data-step'));
      step.classList.remove('active', 'completed');
      if (stepNumber === this.currentStep) {
        step.classList.add('active');
      } else if (stepNumber < this.currentStep) {
        step.classList.add('completed');
      }
    });
    
    // Update step connectors
    const connectors = this.selector.querySelectorAll('.step-connector');
    connectors.forEach((connector, index) => {
      connector.classList.remove('active');
      if (index < this.currentStep - 1) {
        connector.classList.add('active');
      }
    });
    
    // Update step content visibility
    const stepContents = this.selector.querySelectorAll('[data-step-content]');
    stepContents.forEach(content => {
      const contentStep = parseInt(content.getAttribute('data-step-content'));
      content.classList.add('hidden');
      if (contentStep === this.currentStep) {
        content.classList.remove('hidden');
      }
    });
    
    // Update step titles
    const stepTitle1 = this.selector.querySelector('[data-title-step-1]');
    const stepTitle2 = this.selector.querySelector('[data-title-step-2]');
    
    if (stepTitle1 && stepTitle2) {
      stepTitle1.classList.add('hidden');
      stepTitle2.classList.add('hidden');
      
      if (this.currentStep === 1) {
        stepTitle1.classList.remove('hidden');
      } else if (this.currentStep === 2) {
        stepTitle2.classList.remove('hidden');
      }
    }
    
    // Update next step button state
    if (this.currentStep === 1) {
      const nextStepBtn = this.selector.querySelector('[data-action="next-step"]');
      if (nextStepBtn) {
        nextStepBtn.disabled = !this.selectedDate;
      }
    }
    
    // Update back button visibility

  }
  
  confirmSelection() {
    // Ensure frequency is set to 'onetime' if null
    if (!this.selectedFrequency) {
      console.log('Frequency was null, defaulting to onetime');
      this.selectedFrequency = 'onetime';
    }

    console.log('Confirming selection', {
      date: this.selectedDate,
      frequency: this.selectedFrequency
    });
    
    // Double-check that we have both date and frequency selected
    const selectedDateOption = this.selector.querySelector('.date-option.selected');
    const selectedFrequencyOption = this.selector.querySelector('.frequency-option.selected');
    
    // If we don't have a date selected, try to get it from the UI
    if (!this.selectedDate && selectedDateOption) {
      const dayElement = selectedDateOption.querySelector('.date-day');
      const dateElement = selectedDateOption.querySelector('.date-number');
      
      if (dayElement && dateElement) {
        const dayName = dayElement.textContent;
        const dateText = dateElement.textContent;
        this.selectedDate = this.parseDateFromText(dayName, dateText);
      }
    }
    
    // If we don't have a frequency selected, try to get it from the UI
    if (!this.selectedFrequency && selectedFrequencyOption) {
      this.selectedFrequency = selectedFrequencyOption.dataset.frequency;
    }
    
    // Force onetime if still null (fallback)
    if (!this.selectedFrequency) {
      this.selectedFrequency = 'onetime';
    }
    
    // Final validation check
    if (!this.selectedDate) {
      this.currentStep = 1;
      this.updateStepUI();
      this.showValidationError('date');
      return;
    }
    
    if (!this.selectedFrequency) {
      this.showValidationError('frequency');
      return;
    }
    
    // Store selections in localStorage
    if (this.selectedDate) {
      localStorage.setItem('selectedDeliveryDate', this.selectedDate.toISOString());
      
      // Also store a formatted version for display
      const formattedDate = `${this.getDayNameHebrew(this.selectedDate)}, ${this.formatDate(this.selectedDate)}`;
      localStorage.setItem('formattedDeliveryDate', formattedDate);
    }
    
    if (this.selectedFrequency) {
      localStorage.setItem('selectedDeliveryFrequency', this.selectedFrequency);
      
      // Also store a translated version for display
      const translatedFrequency = this.getTranslatedFrequency(this.selectedFrequency);
      localStorage.setItem('translatedDeliveryFrequency', translatedFrequency);
    }
    
    // Update the cart with the selected delivery date and frequency
    this.updateCartWithDeliveryInfo();
    
    // Close the panel - use hideDeliverySelector directly
    this.hideDeliverySelector();
    
    // Check if discount should be applied
    // Get the last order input element to check if cart matches last order
    const lastOrderInput = document.getElementById('lastOrderVariants');
    let shouldApplyDiscount = false;
    
    if (lastOrderInput) {
      // Get the cart comparison status from the UI
      const lastOrderLink = document.querySelector('.last-order-link');
      const isMatching = lastOrderLink && lastOrderLink.classList.contains('is-matching');
      
      // Get the total comparison status
      const totalComparisonElement = lastOrderLink && lastOrderLink.querySelector('.total-comparison');
      const isTotalSameOrGreater = totalComparisonElement && 
                                  totalComparisonElement.textContent.includes('תואם או גבוה יותר');
      
      // Determine if discount should be applied based on configuration
      const discountConfig = {
        requireItemMatch: true,
        requireTotalMatch: true,
        matchType: 'both'  // can be 'both', 'either', 'items_only', 'total_only'
      };
      
      switch(discountConfig.matchType) {
        case 'both':
          shouldApplyDiscount = isMatching && isTotalSameOrGreater;
          break;
        case 'either':
          shouldApplyDiscount = isMatching || isTotalSameOrGreater;
          break;
        case 'items_only':
          shouldApplyDiscount = isMatching;
          break;
        case 'total_only':
          shouldApplyDiscount = isTotalSameOrGreater;
          break;
      }
      
      console.log('Discount application check:', {
        isMatching,
        isTotalSameOrGreater,
        shouldApplyDiscount,
        matchType: discountConfig.matchType
      });
    }
    
    // If this is a one-time purchase, proceed to checkout
    if (this.selectedFrequency === 'onetime') {
      // Add a small delay to allow the cart update to complete
      setTimeout(() => {
        this.proceedToCheckout();
      }, 500);
    } else {
      // For subscriptions, redirect to the subscription checkout with discount if applicable
      setTimeout(() => {
        let checkoutUrl = '/checkout?subscription=true';
        
        // Add discount code to URL if discount should be applied
        if (shouldApplyDiscount) {
          if (this.selectedFrequency === 'weekly') {
            checkoutUrl += '&discount=SUB_WEEKLY_10';
          } else if (this.selectedFrequency === 'biweekly') {
            checkoutUrl += '&discount=SUB_WEEKLY_5';
          }
        } else {
          // If conditions don't match, only add LOYALFAN discount if customer is signed in and has a subscription
          const lastOrderInput = document.getElementById('lastOrderVariants');
          const customerSubscriptionElement = document.querySelector('[data-customer-subscription]');
          
          // Only add LOYALFAN discount if customer is signed in and has a subscription
          if (lastOrderInput && customerSubscriptionElement && customerSubscriptionElement.getAttribute('data-customer-subscription')) {
            checkoutUrl += '&discount=LOYALFAN';
          }
        }
        
        console.log('Redirecting to checkout with URL:', checkoutUrl);
        window.location.href = checkoutUrl;
      }, 500);
    }
  }
  
  proceedToCheckout() {
    // Find the cart form
    const form = document.getElementById('cart');
    if (form) {
      // Add hidden inputs for delivery options
      let dateInput = form.querySelector('input[name="attributes[delivery_date]"]');
      if (!dateInput) {
        dateInput = document.createElement('input');
        dateInput.type = 'hidden';
        dateInput.name = 'attributes[delivery_date]';
        form.appendChild(dateInput);
      }
      dateInput.value = this.formatDate(this.selectedDate);
      
      let frequencyInput = form.querySelector('input[name="attributes[delivery_frequency]"]');
      if (!frequencyInput) {
        frequencyInput = document.createElement('input');
        frequencyInput.type = 'hidden';
        frequencyInput.name = 'attributes[delivery_frequency]';
        form.appendChild(frequencyInput);
      }
      frequencyInput.value = this.selectedFrequency;
      
      // Close the panel before submitting
      this.closePanel();
      
      // Submit the form to go to checkout
      form.action = '/checkout';
      
      // Use a timeout to ensure the panel is fully closed before submitting
      setTimeout(() => {
        form.submit();
      }, 100);
    } else {
      // Fallback if form not found
      window.location.href = '/checkout';
    }
  }
  
  showPanel() {
    // Reset to first step
    this.currentStep = 1;
    
    // Show the selector container
    this.selector.style.display = 'block';
    
    // Show the panel with animation
    setTimeout(() => {
      this.panel.classList.add('show');
    }, 10);
    
    // Show the overlay
    if (this.overlay) {
      this.overlay.style.display = 'block';
    }
    
    document.body.style.overflow = 'hidden';
    
    // Update UI
    this.updateUI();
  }
  
  closePanel() {
    // Instead of calling hideDeliverySelector, implement the closing logic directly
    const panel = this.selector.querySelector('.delivery-date-panel');
    if (panel) {
      // First hide the panel for animation
      panel.classList.remove('show');
      
      // After the transition completes, hide the container and overlay
      setTimeout(() => {
        this.selector.style.display = 'none';
        const overlay = this.selector.querySelector('.delivery-panel-overlay');
        if (overlay) {
          overlay.style.display = 'none';
        }
        document.body.style.overflow = '';
        
        // If we have a selected date, make sure it's still selected when we reopen
        if (this.selectedDate) {
          localStorage.setItem('tempSelectedDate', this.selectedDate.toISOString());
        }
      }, 300); // Match this to the CSS transition duration
    }
  }
  
  updateUI() {
    // Update step visibility
    this.updateStepUI();
    
    // Update the confirm button
    const confirmButton = this.selector.querySelector('[data-action="confirm"]');
    if (confirmButton) {
      // On first step, always enable the button
      if (this.currentStep === 1) {
        confirmButton.disabled = false;
        confirmButton.classList.remove('disabled');
        confirmButton.style.opacity = '1';
      } else if (this.currentStep === 2) {
        // On second step, only enable if frequency is selected
        const isDisabled = !this.selectedFrequency;
        confirmButton.disabled = isDisabled;
        
        if (isDisabled) {
          confirmButton.classList.add('disabled');
          confirmButton.style.opacity = '0.5';
        } else {
          confirmButton.classList.remove('disabled');
          confirmButton.style.opacity = '1';
        }
      }
    }
    
    // Update the confirm button text
    this.updateConfirmButtonText();
    
    // Update the button layout
    this.updateButtonLayout();
  }
  
  displaySavedSelectionsInCart() {
    const selectedDate = localStorage.getItem('selectedDeliveryDate');
    const selectedFrequency = localStorage.getItem('selectedDeliveryFrequency');
    
    // Only proceed if we have selections
    if (!selectedDate && !selectedFrequency) return;
    
    // Clear any existing delivery info displays
    const existingInfoElements = document.querySelectorAll('.cart-delivery-info');
    existingInfoElements.forEach(el => {
      // Find the parent wrapper and remove it entirely
      const wrapper = el.closest('.delivery-info-wrapper');
      if (wrapper) {
        wrapper.remove();
      } else {
        el.remove();
      }
    });
    
    // Create the delivery info display
    const deliveryInfoElement = document.createElement('div');
    deliveryInfoElement.className = 'delivery-info-wrapper';
    deliveryInfoElement.innerHTML = `
      <div class="cart-delivery-info">
        <h4>פרטי משלוח</h4>
        ${selectedDate ? `<p>תאריך משלוח: ${selectedDate}</p>` : ''}
        ${selectedFrequency ? `<p>תדירות: ${this.getFrequencyLabel(selectedFrequency)}</p>` : ''}
        <button class="edit-delivery-info">ערוך</button>
      </div>
    `;
    
    // Find all delivery info placeholders
    const cartPlaceholders = document.querySelectorAll('.cart-delivery-info-placeholder');
    
    // Clone and append to each placeholder
    cartPlaceholders.forEach(placeholder => {
      // Clear any existing content in the placeholder first
      placeholder.innerHTML = '';
      const clone = deliveryInfoElement.cloneNode(true);
      placeholder.appendChild(clone);
    });
    
    // Add event listeners to all edit buttons
    document.querySelectorAll('.edit-delivery-info').forEach(button => {
      button.addEventListener('click', this.handleEditButtonClick.bind(this));
    });
  }
  
  // Helper method to get human-readable frequency label
  getFrequencyLabel(frequency) {
    const labels = {
      'weekly': 'כל שבוע',
      'biweekly': 'כל שבועיים',
      'monthly': 'כל 4 שבועות',
      'onetime': 'רכישה חד פעמית'
    };
    return labels[frequency] || frequency;
  }
  
  // Add a method to handle edit button clicks
  handleEditButtonClick(e) {
    e.preventDefault();
    
    // Find the appropriate delivery selector
    let deliverySelector;
    const isDrawer = e.target.closest('#AjaxCartForm') !== null && !e.target.closest('#AjaxCartForm').closest('.cart-section');
    
    if (isDrawer) {
      // For drawer cart - use the slide-up version
      deliverySelector = document.querySelector('#DrawerDeliveryFrequencySelector');
    } else {
      // For cart page - use the modal version
      deliverySelector = document.querySelector('#DeliveryFrequencySelector.delivery-frequency-modal');
    }
    
    if (deliverySelector) {
      // Show the delivery selector
      deliverySelector.style.display = 'block';
      
      // Get the panel element and overlay
      const panel = deliverySelector.querySelector('.delivery-date-panel');
      const overlay = deliverySelector.querySelector('.delivery-panel-overlay');
      
      // Show the overlay
      if (overlay) {
        overlay.style.display = 'block';
      }
      
      // Add a slight delay to ensure smooth animation
      setTimeout(() => {
        if (panel) {
          panel.classList.add('show');
        }
      }, 10);
      
      document.body.style.overflow = 'hidden';
      
      // Find the instance for this selector
      const instance = window.deliveryFrequencyInstances.find(
        inst => inst.selector === deliverySelector
      );
      
      if (instance) {
        // Reset to first step
        instance.currentStep = 1;
        instance.updateUI();
      }
    }
  }
  
  // Update the showDeliverySelector method to fully reset the UI
  showDeliverySelector(isDrawer = false) {
    console.log("Showing delivery selector");
    
    // Always reset to first step and clear selections
    this.currentStep = 1;
    this.selectedDate = null;
    this.selectedFrequency = null;
    
    // Clear localStorage for temporary selections
    localStorage.removeItem('tempSelectedDate');
    
    // Show the delivery selector panel
    this.selector.style.display = 'block';
    
    // Update the cart total amount
    this.setCartTotal();
    
    // Regenerate date options to reflect current cart state
    this.generateDateOptions();
    
    // Compare cart with last order
    this.compareCartWithLastOrder();
    
    // Show the panel
    this.panel.classList.add('show');
    
    // Show the overlay
    if (this.overlay) {
      this.overlay.style.display = 'block';
    }
    
    document.body.style.overflow = 'hidden';
    
    // Reset UI selections - make sure no date or frequency appears selected
    this.resetUISelections();
    
    // Check if customer has subscription tag and pre-select frequency
    this.checkCustomerSubscription();
    
    // Clear any validation messages
    this.clearValidationMessages();
    
    // Update the UI to reflect the current selections
    this.updateUI();
    
    // Make sure the confirm button text is updated
    this.updateConfirmButtonText();
  }

  // Add a method to check for customer subscription tags
  checkCustomerSubscription() {
    // Look for customer subscription info in the page
    const subscriptionElement = document.querySelector('[data-customer-subscription]');
    
    if (subscriptionElement) {
      const subscription = subscriptionElement.dataset.customerSubscription;
      const translatedSubscription = subscriptionElement.dataset.translatedSubscription;
      console.log("Found customer subscription:", subscription, "Translated:", translatedSubscription);
      
      if (subscription) {
        // Map subscription to frequency option
        let frequency = null;
        
        switch (subscription) {
          case 'weekly':
            frequency = 'weekly';
            break;
          case 'biweekly':
            frequency = 'biweekly';
            break;
          case 'monthly':
            frequency = 'monthly';
            break;
        }
        
        if (frequency) {
          console.log("Pre-selecting frequency:", frequency);
          this.selectedFrequency = frequency;
          
          // Find the matching frequency option and add the existing subscription tag
          const frequencyOption = this.selector.querySelector(`.frequency-option[data-frequency="${frequency}"]`);
          if (frequencyOption) {
            // Mark the subscription option as selected
            frequencyOption.classList.add('selected');
            
            // Create or get the subscription info row
            let subscriptionInfoRow = frequencyOption.querySelector('.subscription-info-row');
            if (!subscriptionInfoRow) {
              subscriptionInfoRow = document.createElement('div');
              subscriptionInfoRow.className = 'subscription-info-row';
              subscriptionInfoRow.style.display = 'flex';
              subscriptionInfoRow.style.flexDirection = 'column';
              subscriptionInfoRow.style.alignItems = 'flex-start';
              frequencyOption.appendChild(subscriptionInfoRow);
            }
            
            // Add the subscription status container
            const subscriptionStatusContainer = document.createElement('div');
            subscriptionStatusContainer.className = 'subscription-status-container';
            subscriptionStatusContainer.style.display = 'flex';
            subscriptionStatusContainer.style.flexDirection = 'column';
            subscriptionStatusContainer.style.alignItems = 'flex-start';
            subscriptionStatusContainer.style.marginRight = 'auto';
            
            // Add the existing subscription tag
            if (!subscriptionInfoRow.querySelector('.existing-subscription-tag')) {
              // Create a link element instead of a span
              const existingTag = document.createElement('a');
              existingTag.className = 'existing-subscription-tag';
              existingTag.textContent = 'עדכון מנוי קיים';
              existingTag.href = '/account';
              existingTag.style.marginBottom = '5px';
              existingTag.style.textDecoration = 'none';
              existingTag.style.display = 'inline-block';
              subscriptionStatusContainer.appendChild(existingTag);

              // Remove the separate account link since it's now combined with the tag
              
              // Add the container to the subscription info row
              subscriptionInfoRow.appendChild(subscriptionStatusContainer);
            }
            
            // Add a lock icon
            if (!subscriptionInfoRow.querySelector('.lock-icon')) {
              const lockIcon = document.createElement('span');
              lockIcon.className = 'lock-icon';
              lockIcon.innerHTML = '🔒';
              subscriptionInfoRow.appendChild(lockIcon);
            }
            
            // Add the subscription note
            if (!subscriptionInfoRow.querySelector('.subscription-note')) {
              const note = document.createElement('span');
              note.className = 'subscription-note';
              note.textContent = 'מוגדר לפי המנוי שלך';
              subscriptionInfoRow.appendChild(note);
            }
          }
          
          // Disable other subscription options except one-time and the customer's subscription
          const frequencyOptions = this.selector.querySelectorAll('.frequency-option');
          frequencyOptions.forEach(option => {
            const optionFrequency = option.dataset.frequency;
            if (optionFrequency !== frequency && optionFrequency !== 'onetime') {
              option.classList.add('disabled');
              option.style.opacity = '0.5';
              option.style.cursor = 'not-allowed';
              
              // Remove click event listener
              const newOption = option.cloneNode(true);
              option.parentNode.replaceChild(newOption, option);
            }
          });
          
          // Enable the confirm button since we have a pre-selected frequency
          const confirmButton = this.selector.querySelector('[data-action="confirm"]');
          if (confirmButton) {
            confirmButton.disabled = false;
          }
        }
      }
    }
  }

  // Method to disable frequency selection
  disableFrequencySelection() {
    const frequencyOptions = this.selector.querySelectorAll('.frequency-option');
    
    frequencyOptions.forEach(option => {
      const frequency = option.dataset.frequency;
      
      // Allow clicking on one-time purchase or the selected frequency (existing subscription)
      if (frequency !== this.selectedFrequency && frequency !== 'onetime') {
        option.classList.add('disabled');
        option.style.opacity = '0.5';
        option.style.cursor = 'not-allowed';
        
        // Remove click event listener
        const newOption = option.cloneNode(true);
        option.parentNode.replaceChild(newOption, option);
      } else if (frequency === this.selectedFrequency) {
        option.classList.add('selected');
        option.classList.add('locked');
        
        // Create or get the subscription info row
        let subscriptionInfoRow = option.querySelector('.subscription-info-row');
        if (!subscriptionInfoRow) {
          subscriptionInfoRow = document.createElement('div');
          subscriptionInfoRow.className = 'subscription-info-row';
          subscriptionInfoRow.style.display = 'flex';
          subscriptionInfoRow.style.flexDirection = 'column';
          subscriptionInfoRow.style.alignItems = 'flex-start';
          option.appendChild(subscriptionInfoRow);
        }
        
        // Add the subscription status container
        const subscriptionStatusContainer = document.createElement('div');
        subscriptionStatusContainer.className = 'subscription-status-container';
        subscriptionStatusContainer.style.display = 'flex';
        subscriptionStatusContainer.style.flexDirection = 'column';
        subscriptionStatusContainer.style.alignItems = 'flex-start';
        subscriptionStatusContainer.style.marginRight = 'auto';
        
        // Add the existing subscription tag if not already present
        if (!subscriptionInfoRow.querySelector('.existing-subscription-tag')) {
          const existingTag = document.createElement('a');
          existingTag.className = 'existing-subscription-tag';
          existingTag.textContent = 'עדכון מנוי קיים';
          existingTag.href = '/account';
          existingTag.style.marginBottom = '5px';
          existingTag.style.textDecoration = 'none';
          existingTag.style.display = 'inline-block';
          subscriptionStatusContainer.appendChild(existingTag);
          subscriptionInfoRow.appendChild(subscriptionStatusContainer);
        }
      }
    });
    
    // Skip to the next step if we're on the frequency step
    if (this.currentStep === 2) {
      this.goToNextStep();
    }
  }

  // Add this method to check for out-of-stock items in the cart
  checkCartForOutOfStockItems() {
    console.log("Checking cart for out-of-stock items");
    
    // First check for out-of-stock badges in the cart
    const outOfStockBadges = document.querySelectorAll('.cart-item__badge--delivery');
    if (outOfStockBadges.length > 0) {
      console.log("Found out-of-stock badges:", outOfStockBadges.length);
      return true;
    }
    
    // Then check for cart items with out-of-stock class or tag
    const cartItems = document.querySelectorAll('.cart-item');
    console.log(`Found ${cartItems.length} cart items`);
    
    for (const item of cartItems) {
      // Check for out-of-stock class
      if (item.classList.contains('out-of-stock')) {
        console.log("Found item with out-of-stock class");
        return true;
      }
      
      // Check for data attribute that might indicate out-of-stock
      if (item.dataset.outOfStock === 'true') {
        console.log("Found item with out-of-stock data attribute");
        return true;
      }
    }
    
    console.log("No out-of-stock items found");
    return false;
  }

  // Add method to check if any cart item has the one-time delivery tag
  hasOneTimeDeliveryTag() {
    console.log("Checking cart for one-time delivery tag items");
    
    // Get the tag from settings
    const oneTimeDeliveryTag = window.deliverySettings?.oneTimeDatesTags || '';
    
    if (!oneTimeDeliveryTag || oneTimeDeliveryTag === 'none') {
      console.log("No one-time delivery tag configured");
      return false;
    }
    
    // Check cart items for the tag
    const cartItems = document.querySelectorAll('.cart-item');
    console.log(`Found ${cartItems.length} cart items, checking for tag: ${oneTimeDeliveryTag}`);
    
    // Also check data-out-of-stock attribute if the tag might be stored there
    for (const item of cartItems) {
      // Check for class, data-tags attribute, or any other attribute that might contain tag info
      if (item.classList.contains(oneTimeDeliveryTag) || 
          (item.dataset.tags && item.dataset.tags.includes(oneTimeDeliveryTag)) ||
          item.hasAttribute(`data-tag-${oneTimeDeliveryTag}`)) {
        console.log(`Found item with one-time delivery tag: ${oneTimeDeliveryTag}`);
        return true;
      }
     
   
      // Some cart items might be marked with a custom attribute
      
      if (item.dataset.productId) {
        // Try to find product tag data in related elements
        const productTagEls = document.querySelectorAll(`[ data-one-time-delivery="true"]`);
        for (const tagEl of productTagEls) {
          if (tagEl.dataset.oneTimeDelivery && tagEl.dataset.oneTimeDelivery==="true") {
            console.log(`Found product with one-time delivery tag: ${oneTimeDeliveryTag}`);
            return true;
          }
        }
      }
    }
    
    console.log("No items with one-time delivery tag found");
    return false;
  }

  // Add this method to get the next available delivery date from our liquid snippet
  getNextAvailableDeliveryDate() {
    console.log("Getting next available delivery date");
    const deliveryBadge = document.querySelector('.cart-item__badge--delivery');
    console.log("Found delivery badge:", deliveryBadge);
    
    if (deliveryBadge) {
      const badgeText = deliveryBadge.textContent.trim();
      console.log("Badge text:", badgeText);
      
      const dateMatch = badgeText.match(/(\d{2}\/\d{2})$/);
      console.log("Date match:", dateMatch);
      
      if (dateMatch && dateMatch[1]) {
        const dateParts = dateMatch[1].split('/');
        const day = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
        
        console.log(`Parsed date: day=${day}, month=${month}`);
        
        // Create a date object for this date
        const currentYear = new Date().getFullYear();
        const nextDeliveryDate = new Date(currentYear, month, day);
        
        console.log("Created date object:", nextDeliveryDate);
        
        // If the date is in the past (e.g., December → January transition), add a year
        if (nextDeliveryDate < new Date()) {
          nextDeliveryDate.setFullYear(currentYear + 1);
          console.log("Date was in the past, added a year:", nextDeliveryDate);
        }
        
        return nextDeliveryDate;
      }
    }
    
    console.log("Could not find or parse delivery date");
    return null;
  }

  // Add logging to getDayName
  getDayName(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = days[date.getDay()];
   // console.log(`Getting day name for ${date}: ${dayName}`);
    return dayName;
  }

  // Add this helper method to get Hebrew day names
  getDayNameHebrew(date) {
    const dayNames = {
      0: "יום ראשון",
      1: "יום שני",
      2: "יום שלישי",
      3: "יום רביעי",
      4: "יום חמישי",
      5: "יום שישי",
      6: "יום שבת"
    };
    return dayNames[date.getDay()];
  }

  // Enhance the handleCartUpdate method to always reset the date when cart changes
  handleCartUpdate() {
    console.log('Cart updated, checking for changes');
    
    // Get current cart state
    const currentOutOfStock = this.checkCartForOutOfStockItems();
    const currentItemCount = document.querySelectorAll('.cart-item').length;
    
    // Compare with previous state
    const hasStateChanged = currentOutOfStock !== this.cartState.hasOutOfStock ||
                          currentItemCount !== this.cartState.itemCount;
    
    if (hasStateChanged) {
      console.log('Cart state changed, updating UI');
      
      // Update stored state
    this.cartState = {
        hasOutOfStock: currentOutOfStock,
        itemCount: currentItemCount
    };
    
      // Regenerate date options
      this.generateDateOptions();
    
      // Update cart total
    this.setCartTotal();
    
      // Compare cart with last order and update UI
      this.compareCartWithLastOrder();
      
      // Update UI
      this.updateUI();
    
      // Update any displayed delivery info in the cart
    this.displaySavedSelectionsInCart();
    }

    // Always check cart comparison regardless of state change
    // This ensures we catch any subtle changes that might affect matching
    this.compareCartWithLastOrder();
  }

  // Add a method to update the confirm button text based on current step
  updateConfirmButtonText() {
    // Support both old and new button classes
    const confirmButton = this.selector.querySelector('[data-action="confirm"]');
    const checkoutBtnNew = this.selector.querySelector('.checkout-btn-new');
    
    if (confirmButton && !checkoutBtnNew) {
      confirmButton.innerHTML = 'לתשלום <span class="arrow">←</span>';
    }
    // New button keeps its original text from HTML
  }

  // Add a method to show validation errors
  showValidationError(type) {
    if (type === 'date') {
      // Flash the date options or show a message (support both old and new classes)
      const dateOptions = this.selector.querySelector('.date-grid-new, .date-options');
      if (dateOptions) {
        dateOptions.classList.add('validation-error');
        
        // Add a validation message
        let errorMsg = this.selector.querySelector('.validation-message');
        if (!errorMsg) {
          errorMsg = document.createElement('div');
          errorMsg.className = 'validation-message';
          errorMsg.style.cssText = 'text-align: center; color: #dc2626; font-size: 14px; margin-top: 12px; font-weight: 500;';
          errorMsg.textContent = 'אנא בחר תאריך משלוח';
          dateOptions.parentNode.insertBefore(errorMsg, dateOptions.nextSibling);
        } else {
          // Make sure the message is visible
          errorMsg.style.display = 'block';
          errorMsg.textContent = 'אנא בחר תאריך משלוח';
        }
        
        // Scroll to the date options
        dateOptions.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Remove the error styling after animation
        setTimeout(() => {
          dateOptions.classList.remove('validation-error');
        }, 1000);
      }
    } else if (type === 'frequency') {
      // Flash the frequency options or show a message
      const frequencyOptions = this.selector.querySelector('.frequency-options');
      if (frequencyOptions) {
        frequencyOptions.classList.add('validation-error');
        
        // Add a validation message
        let errorMsg = this.selector.querySelector('.validation-message');
        if (!errorMsg) {
          errorMsg = document.createElement('div');
          errorMsg.className = 'validation-message';
          errorMsg.textContent = 'אנא בחר תדירות משלוח';
          frequencyOptions.parentNode.insertBefore(errorMsg, frequencyOptions.nextSibling);
        } else {
          // Make sure the message is visible
          errorMsg.style.display = 'block';
          errorMsg.textContent = 'אנא בחר תדירות משלוח';
        }
        
        // Scroll to the frequency options
        frequencyOptions.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Remove the error styling after animation
        setTimeout(() => {
          frequencyOptions.classList.remove('validation-error');
        }, 1000);
      }
    }
  }

  // Update the hideDeliverySelector method to hide the panel
  hideDeliverySelector() {
    this.closePanel();
  }

  // Helper method to get translated frequency
  getTranslatedFrequency(frequency) {
    const translations = {
      'weekly': 'כל שבוע',
      'biweekly': 'כל שבועיים',
      'monthly': 'פעם בחודש',
      'onetime': 'חד פעמי'
    };
    
    return translations[frequency] || frequency;
  }

  // Update the clearValidationMessages method to be even more thorough
  clearValidationMessages() {
    console.log('Clearing validation messages');
    
    // Remove any validation error classes
    const errorElements = this.selector.querySelectorAll('.validation-error');
    errorElements.forEach(element => {
      element.classList.remove('validation-error');
    });
    
    // Remove any validation message elements - more aggressive approach
    const messageElements = this.selector.querySelectorAll('.validation-message');
    messageElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    
    // Also check for any elements with validation-related text content
    const allElements = this.selector.querySelectorAll('*');
    allElements.forEach(element => {
      if (element.textContent === 'אנא בחר תאריך משלוח' || 
          element.textContent === 'אנא בחר תדירות משלוח') {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      }
    });
    
    // Remove any inline validation messages that might have been added
    const dateOptions = this.selector.querySelector('.date-options');
    const frequencyOptions = this.selector.querySelector('.frequency-options');
    
    if (dateOptions) {
      dateOptions.classList.remove('validation-error');
      const dateValidationMsg = dateOptions.nextElementSibling;
      if (dateValidationMsg && (dateValidationMsg.classList.contains('validation-message') || 
                               dateValidationMsg.textContent === 'אנא בחר תאריך משלוח')) {
        dateValidationMsg.remove();
      }
    }
    
    if (frequencyOptions) {
      frequencyOptions.classList.remove('validation-error');
      const freqValidationMsg = frequencyOptions.nextElementSibling;
      if (freqValidationMsg && (freqValidationMsg.classList.contains('validation-message') || 
                               freqValidationMsg.textContent === 'אנא בחר תדירות משלוח')) {
        freqValidationMsg.remove();
      }
    }
    
    // Check for any elements with the text content and remove them
    document.querySelectorAll('*').forEach(el => {
      if (el.textContent === 'אנא בחר תאריך משלוח' || el.textContent === 'אנא בחר תדירות משלוח') {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    });
  }

  // Add a method to reset UI selections
  resetUISelections() {
    // Clear date selections
    const dateOptions = this.selector.querySelectorAll('.date-option');
    dateOptions.forEach(option => {
      option.classList.remove('selected');
    });
    
    // Clear frequency selections
    const frequencyOptions = this.selector.querySelectorAll('.frequency-option');
    frequencyOptions.forEach(option => {
      option.classList.remove('selected');
    });
    
    // Disable next step button
    const nextStepBtn = this.selector.querySelector('[data-action="next-step"]');
    if (nextStepBtn) {
      nextStepBtn.disabled = true;
    }
    
    // Reset shipping labels to default state
    if (this.freeShippingLabel && this.paidShippingLabel) {
      this.freeShippingLabel.classList.add('hidden');
      this.paidShippingLabel.classList.remove('hidden');
    }
  }

  // Update the updateButtonLayout method to check for selected dates in the UI
  updateButtonLayout() {
    const confirmSection = this.selector.querySelector('.confirm-section');
    const confirmButton = this.selector.querySelector('[data-action="confirm"]');
    
    if (!confirmSection || !confirmButton) return;
    
    // Create a button container if it doesn't exist
    let buttonContainer = confirmSection.querySelector('.button-container');
    if (!buttonContainer) {
      buttonContainer = document.createElement('div');
      buttonContainer.className = 'button-container';
      
      // Move the confirm button into the container
      const originalButton = confirmButton.cloneNode(true);
      confirmButton.parentNode.replaceChild(buttonContainer, confirmButton);
      buttonContainer.appendChild(originalButton);
      
      // Add event listener to the new button
      originalButton.addEventListener('click', (event) => {
        event.preventDefault();
        
        // Try to set the selected date from the UI if it's not already set
        if (!this.selectedDate) {
          this.setSelectedDateFromUI();
        }
        
        console.log('Confirm button clicked', {
          currentStep: this.currentStep,
          selectedDate: this.selectedDate,
          selectedFrequency: this.selectedFrequency
        });
        
        // Validate date
        const selectedDateOption = this.selector.querySelector('.date-option.selected');
        if (!this.selectedDate && !selectedDateOption) {
          this.showValidationError('date');
          return false;
        }
        
        // Direct confirmation
        this.confirmSelection();
      });
    }
    
    // Try to set the selected date from the UI if it's not already set
    if (!this.selectedDate) {
      this.setSelectedDateFromUI();
    }
    
    // Remove any existing back buttons to prevent duplicates
    const existingBackButtons = buttonContainer.querySelectorAll('.back-btn');
    existingBackButtons.forEach(btn => btn.remove());
    
    // Create a new back button
    const backButton = document.createElement('button');
    backButton.className = 'back-btn';
    backButton.innerHTML = '<span class="arrow back-arrow">→</span> חזרה';
    backButton.setAttribute('data-action', 'back-step');
    backButton.setAttribute('type', 'button'); // Ensure it's not a submit button
    
    // Add event listener with direct function call
    backButton.onclick = () => {
      console.log('Back button clicked - direct call');
      this.currentStep = 1; // Force to step 1
      this.clearValidationMessages();
      this.updateStepUI();
      this.updateConfirmButtonText();
      this.updateButtonLayout();
    };
    
    // Insert at the beginning of button container
    buttonContainer.insertBefore(backButton, buttonContainer.firstChild);
    
    // Get the new confirm button reference
    const newConfirmButton = buttonContainer.querySelector('[data-action="confirm"]');
    
    // Check if we have selections and update button state
    const hasDateSelected = this.selectedDate || this.selector.querySelector('.date-option.selected');
    const hasFrequencySelected = this.selectedFrequency || this.selector.querySelector('.frequency-option.selected');
    
    if (newConfirmButton) {
      // Enable the button if we have the appropriate selection for the current step
      if ((this.currentStep === 1 && hasDateSelected) || 
          (this.currentStep === 2 && hasFrequencySelected)) {
        newConfirmButton.disabled = false;
        newConfirmButton.classList.remove('disabled');
        newConfirmButton.style.opacity = '1';
      }
      
      // Update layout based on current step
      if (this.currentStep === 1) {
        // First step: full width button, hide back button
        backButton.style.display = 'none';
        newConfirmButton.style.width = '100%';
      } else {
        // Second step: reduced width button, show back button
        backButton.style.display = 'inline-block';
        newConfirmButton.style.width = '80%';
      }
    }
  }

  // Add a method to directly set the selected date from the UI
  setSelectedDateFromUI() {
    const selectedDateOption = this.selector.querySelector('.date-option.selected');
    if (selectedDateOption) {
      const dayElement = selectedDateOption.querySelector('.date-day');
      const dateElement = selectedDateOption.querySelector('.date-number');
      
      if (dayElement && dateElement) {
        const dayName = dayElement.textContent;
        const dateText = dateElement.textContent;
        const selectedDate = this.parseDateFromText(dayName, dateText);
        
        if (selectedDate) {
          console.log("Setting selected date from UI:", selectedDate);
          this.selectedDate = selectedDate;
          return true;
        }
      }
    }
    return false;
  }

  // Add the missing updateCartWithDeliveryInfo function
  updateCartWithDeliveryInfo() {
    console.log('Updating cart with delivery info', {
      date: this.selectedDate,
      frequency: this.selectedFrequency
    });
    
    // Format the date for display
    const formattedDate = this.selectedDate ? 
      `${this.getDayNameHebrew(this.selectedDate)}, ${this.formatDate(this.selectedDate)}` : '';
    
    // Get the translated frequency
    const translatedFrequency = this.selectedFrequency ? 
      this.getTranslatedFrequency(this.selectedFrequency) : '';
    
    // Create or update cart attributes
    const cartUpdateData = {
      attributes: {
        'Delivery_Date': this.selectedDate ? this.selectedDate.toISOString() : '',
        'Formatted_Delivery_Date': formattedDate,
        'Delivery Frequency': this.selectedFrequency || '',
        'Translated_Frequency': translatedFrequency
      }
    };
    
    
    // Update the cart
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cartUpdateData)
    })
    .then(response => response.json())
    .then(cart => {
      console.log('Cart updated with delivery info', cart);
      
      // Dispatch an event to notify other components
      document.dispatchEvent(new CustomEvent('delivery-info-updated', {
        detail: {
          date: formattedDate,
          frequency: translatedFrequency
        }
      }));
    })
    .catch(error => {
      console.error('Error updating cart with delivery info', error);
    });
  }

  // Add a method to get discount information for each frequency option
  getDiscountInfo(frequency) {
    const discountInfo = {
      'weekly': '10% הנחה על ההזמנה הבאה',
      'biweekly': '5% הנחה על ההזמנה הבאה',
      'monthly': 'הפתעה טעימה',
      'onetime': ''
    };
    
    return discountInfo[frequency] || '';
  }

  // Update the shipping label based on selected frequency
  updateShippingLabel() {
    const freeShippingLabel = this.selector.querySelector('[data-free-shipping]');
    const paidShippingLabel = this.selector.querySelector('[data-paid-shipping]');
    
    // Hide both labels initially
    freeShippingLabel.classList.add('hidden');
    paidShippingLabel.classList.add('hidden');
    
    // Show appropriate label based on frequency
    if (this.selectedFrequency) {
      let discountText = '';
      
      // Set discount text based on frequency
      if (this.selectedFrequency === 'weekly') {
        discountText = 'חסכו 10% להזמנות חוזרות';
        paidShippingLabel.textContent = discountText;
        paidShippingLabel.classList.remove('hidden');
      } else if (this.selectedFrequency === 'biweekly') {
        discountText = 'חסכו 5% להזמנות חוזרות';
        paidShippingLabel.textContent = discountText;
        paidShippingLabel.classList.remove('hidden');
      } else if (this.selectedFrequency === 'monthly') {
        discountText = 'הפתעה טעימה בהזמנות חוזרות';
        paidShippingLabel.textContent = discountText;
        paidShippingLabel.classList.remove('hidden');
      }
    }
  }

  handleDeliveryDatesReload(event) {
    console.log('Handling delivery dates reload event:', event.detail);
    
    // Check if the cart state has changed
    const currentOutOfStock = this.checkCartForOutOfStockItems();
    const currentItemCount = document.querySelectorAll('.cart-item').length;
    
    const cartChanged = 
      currentOutOfStock !== this.cartState.hasOutOfStock || 
      currentItemCount !== this.cartState.itemCount;
    
    // Update the cart state
    this.cartState = {
      hasOutOfStock: currentOutOfStock,
      itemCount: currentItemCount
    };
    
    // If the cart has changed, regenerate the date options
    if (cartChanged) {
      console.log('Cart state changed, regenerating date options');
      
      // Reset selected date if cart has changed significantly
      if (event.detail.action === 'remove' || event.detail.action === 'refresh') {
        this.selectedDate = null;
        localStorage.removeItem('tempSelectedDate');
        localStorage.removeItem('selectedDeliveryDate');
      }
      
      // Regenerate date options
      this.generateDateOptions();
      
      // Update the UI
      this.updateUI();
      
      // Update any displayed delivery info in the cart
      this.displaySavedSelectionsInCart();
    }
  }

  getCutoffHour(dayName) {
    return window.deliverySettings?.cutoffHours?.[dayName] || 1;
  }

  compareCartWithLastOrder() {
    console.log('Comparing cart with last order...');
    
    // Get the last order variants from the hidden input
    const lastOrderInput = document.getElementById('lastOrderVariants');
    if (!lastOrderInput) {
      console.log('No last order input found');
      return;
    }
    
    // Check if customer has a subscription
    const customerSubscriptionElement = document.querySelector('[data-customer-subscription]');
    if (!customerSubscriptionElement || !customerSubscriptionElement.getAttribute('data-customer-subscription')) {
      console.log('Customer does not have a subscription');
      return;
    }
    
    // Get the cart comparison status element and make sure it's visible
    const cartComparisonStatus = this.selector.querySelector('.cart-comparison-status');
    if (cartComparisonStatus) {
      cartComparisonStatus.style.display = 'block';
    }
    
    console.log('Last order input:', lastOrderInput);
    const lastOrderString = lastOrderInput.value;
    console.log('Last order string:', lastOrderString);
    
    if (!lastOrderString) {
      console.log('No last order string');
      return;
    }
    
    // Parse the last order variants
    const lastOrderVariants = {};
    lastOrderString.split(',').forEach(item => {
      const [variantId, quantity] = item.split(':');
      lastOrderVariants[variantId] = parseInt(quantity);
    });
    console.log('Last order variants:', lastOrderVariants);
    
    // Get the current cart items
    const cartItems = document.querySelectorAll('.cart-form-item, .cart-item, [data-cart-item], [data-js-cart-item]');
    console.log('Cart items:', cartItems);
    
    if (!cartItems.length) {
      console.log('No cart items found');
      return;
    }
    
    // Check if there's already a discount applied to the cart
    const existingDiscounts = document.querySelectorAll('.cart__discounts');
    let hasExistingDiscount = false;
    
    existingDiscounts.forEach(discount => {
      // If there's a discount that's not our subscription discount
      if (discount.textContent && !discount.textContent.includes('Subscription_Discount') && !discount.classList.contains('hide-loyalfan-discount')) {
        hasExistingDiscount = true;
      }
    });
    
    console.log('Has existing discount:', hasExistingDiscount);
    
    // Create a map of current cart items
    const currentCartVariants = {};
    cartItems.forEach(item => {
      let variantId = item.id;
      
      // Try to get variant ID from different possible attributes
      if (!variantId) {
        variantId = item.getAttribute('data-variant-id') || 
                   item.getAttribute('data-id') || 
                   item.getAttribute('data-item-id') ||
                   item.getAttribute('data-cart-item');
      }
      
      // If still no variant ID, try to find it in a child element
      if (!variantId) {
        const variantIdElement = item.querySelector('[data-variant-id]') || 
                                item.querySelector('[data-id]') || 
                                item.querySelector('[data-item-id]');
        if (variantIdElement) {
          variantId = variantIdElement.getAttribute('data-variant-id') || 
                     variantIdElement.getAttribute('data-id') || 
                     variantIdElement.getAttribute('data-item-id');
        }
      }
      
      // Extract just the number from the variant ID if it contains other text
      if (variantId && typeof variantId === 'string') {
        const match = variantId.match(/\d+/);
        if (match) {
          variantId = match[0];
        }
      }
      
      if (variantId) {
        // Try to get quantity from different possible elements
        let quantity = 1;
        const quantityInput = item.querySelector('input[name="quantity"], [data-quantity-input], [data-item-quantity]');
        if (quantityInput) {
          quantity = parseInt(quantityInput.value || quantityInput.getAttribute('value') || 1);
        } else {
          const quantityElement = item.querySelector('[data-quantity], [data-item-quantity]');
          if (quantityElement) {
            quantity = parseInt(quantityElement.textContent || quantityElement.getAttribute('data-quantity') || 1);
          }
        }
        
        currentCartVariants[variantId] = (currentCartVariants[variantId] || 0) + quantity;
      }
    });
    console.log('Current cart variants:', currentCartVariants);
    
    // Compare the variants
    let isMatching = true;
    let hasAllLastOrderItems = true;
    
    // Check if all last order items are in the current cart with at least the same quantity
    for (const [variantId, quantity] of Object.entries(lastOrderVariants)) {
      if (!currentCartVariants[variantId] || currentCartVariants[variantId] < quantity) {
        hasAllLastOrderItems = false;
        break;
      }
    }
    
    // Check if the current cart has exactly the same items as the last order
    isMatching = hasAllLastOrderItems && Object.keys(lastOrderVariants).length === Object.keys(currentCartVariants).length;
    
    // Get the last order total from the data attribute
    const lastOrderTotal = parseFloat(lastOrderInput.getAttribute('data-last-order-total') || 0);
    console.log('Last order total:', lastOrderTotal);
    
    // Get the current cart total
    let cartItemsSubtotal = 0;
    const cartSubtotalElement = document.querySelector('[data-cart-items-subtotal]');
    if (cartSubtotalElement) {
      const subtotalText = cartSubtotalElement.textContent.replace(/[^0-9.]/g, '');
      cartItemsSubtotal = parseFloat(subtotalText);
    } else {
      const cartTotalElement = document.querySelector('[data-cart-total]');
      if (cartTotalElement) {
        const totalText = cartTotalElement.textContent.replace(/[^0-9.]/g, '');
        cartItemsSubtotal = parseFloat(totalText);
      }
    }
    this.cartTotal = cartItemsSubtotal;
    console.log('Current cart total:', this.cartTotal);
    
    // Check if the current cart total is greater than or equal to the last order total
    const isTotalSameOrGreater = this.cartTotal >= lastOrderTotal;
    console.log('Is total same or greater:', isTotalSameOrGreater);
    
    // Configuration for discount application
    const discountConfig = {
      requireItemMatch: true,
      requireTotalMatch: true,
      matchType: 'both' // 'both', 'either', 'items_only', 'total_only'
    };
    
    // Determine if discount should be applied based on configuration
    let shouldApplyDiscount = false;
    
    if (discountConfig.matchType === 'both') {
      shouldApplyDiscount = isMatching && isTotalSameOrGreater;
    } else if (discountConfig.matchType === 'either') {
      shouldApplyDiscount = (discountConfig.requireItemMatch && isMatching) || 
                           (discountConfig.requireTotalMatch && isTotalSameOrGreater);
    } else if (discountConfig.matchType === 'items_only') {
      shouldApplyDiscount = isMatching;
    } else if (discountConfig.matchType === 'total_only') {
      shouldApplyDiscount = isTotalSameOrGreater;
    }
    
    // Don't apply discount if there's already a discount
    if (hasExistingDiscount) {
      shouldApplyDiscount = false;
    }
    
    console.log('Should apply discount:', shouldApplyDiscount);
    
    // Update the UI based on the comparison result
    const lastOrderLink = this.selector.querySelector('.last-order-link');
    const existingSubscription = this.selector.querySelector('.existing-subscription');
    
    if (lastOrderLink) {
      const statusText = lastOrderLink.querySelector('.text');
      
      if (isMatching) {
        lastOrderLink.classList.add('is-matching');
        lastOrderLink.classList.remove('not-matching');
        statusText.innerHTML = '✓ תואם להזמנה האחרונה שלך';
        statusText.style.color = '#4CAF50';
        
        // Add total comparison message
        let totalComparisonMessage = '';
        if (isTotalSameOrGreater) {
          totalComparisonMessage = `<div style="color: #4CAF50; margin-top: 5px;">✓ הסכום הנוכחי (${this.cartTotal} ₪) שווה או גבוה מההזמנה האחרונה (${lastOrderTotal} ₪)</div>`;
        } else {
          totalComparisonMessage = `<div style="color: #ff385c; margin-top: 5px;">× הסכום הנוכחי (${this.cartTotal} ₪) נמוך מההזמנה האחרונה (${lastOrderTotal} ₪)</div>`;
        }
        
        // Only show the total comparison message if we don't have an existing discount
        if (!hasExistingDiscount) {
          lastOrderLink.insertAdjacentHTML('afterend', totalComparisonMessage);
        }
      } else {
        lastOrderLink.classList.add('not-matching');
        lastOrderLink.classList.remove('is-matching');
        statusText.innerHTML = '× לא תואם להזמנה האחרונה שלך';
        statusText.style.color = '#ff385c';
        
        // Remove any existing total comparison message
        const existingTotalMessage = this.selector.querySelector('.last-order-link + div');
        if (existingTotalMessage) {
          existingTotalMessage.remove();
        }
      }
    }
    
    if (existingSubscription) {
      if (isMatching) {
        existingSubscription.textContent = 'חידוש הזמנה למנוי';
      } else {
        existingSubscription.textContent = 'עדכון מנוי קיים';
      }
    }
    
    // Update the subscription tag text in the frequency options
    const subscriptionTag = this.selector.querySelector('.existing-subscription-tag');
    if (subscriptionTag) {
      if (isMatching) {
        subscriptionTag.textContent = 'חידוש הזמנה למנוי';
      } else {
        subscriptionTag.textContent = 'עדכון מנוי קיים';
      }
    }
    
    // Also update any subscription tags in the frequency options
    const frequencyOptionsTags = document.querySelectorAll('.frequency-option .existing-subscription-tag');
    frequencyOptionsTags.forEach(tag => {
      if (isMatching) {
        tag.textContent = 'חידוש הזמנה למנוי';
      } else {
        tag.textContent = 'עדכון מנוי קיים';
      }
    });
    
    // Apply or remove discount based on the comparison result
    if (shouldApplyDiscount && this.selectedFrequency) {
      // Apply discount based on frequency
      let discountPercentage = 0;
      if (this.selectedFrequency === 'weekly') {
        discountPercentage = 10;
      } else if (this.selectedFrequency === 'biweekly') {
        discountPercentage = 5;
      }
      
      if (discountPercentage > 0) {
        // Calculate discount amount
        const discountAmount = (this.cartTotal * discountPercentage / 100).toFixed(2);
        const discountedTotal = (this.cartTotal - discountAmount).toFixed(2);
        
        // Update cart attributes with discount information
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              'Subscription_Discount_Percentage': `${discountPercentage}%`,
              'Subscription_Discount_Amount': discountAmount,
              'Original_Total': this.cartTotal.toFixed(2),
              'Discounted_Total': discountedTotal
            }
          })
        })
        .then(response => response.json())
        .then(data => {
          console.log('Cart updated with discount:', data);
          
          // Update the displayed total
          if (this.totalAmountElement) {
            this.totalAmountElement.textContent = discountedTotal;
          }
          
          // Show discount message
          const discountMessage = `<div style="color: #4CAF50; margin-top: 5px; font-weight: 500;">חסכת ${discountAmount} ₪ (${discountPercentage}% הנחה)</div>`;
          const existingDiscountMessage = this.selector.querySelector('.discount-message');
          
          if (existingDiscountMessage) {
            existingDiscountMessage.innerHTML = discountMessage;
          } else {
            const totalLabel = this.selector.querySelector('.total-label');
            if (totalLabel) {
              totalLabel.insertAdjacentHTML('afterend', `<div class="discount-message">${discountMessage}</div>`);
            }
          }
        })
        .catch(error => {
          console.error('Error updating cart with discount:', error);
        });
      }
    } else {
      // Remove any existing discount
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            'Subscription_Discount_Percentage': null,
            'Subscription_Discount_Amount': null,
            'Original_Total': null,
            'Discounted_Total': null
          }
        })
      })
      .then(response => response.json())
      .then(data => {
        console.log('Cart updated, discount removed:', data);
        
        // Reset the displayed total to the original amount
        if (this.totalAmountElement) {
          this.totalAmountElement.textContent = this.cartTotal.toFixed(2);
        }
        
        // Remove discount message
        const discountMessage = this.selector.querySelector('.discount-message');
        if (discountMessage) {
          discountMessage.remove();
        }
      })
      .catch(error => {
        console.error('Error updating cart, removing discount:', error);
      });
    }
    
    return isMatching;
  }

  // Add a method to check if a date is a blackout date
  isBlackoutDate(date) {
    // Get blackout dates from settings
    const blackoutDates = window.deliverySettings?.blackoutDates || [];

    // If no blackout dates or contains 'none', return false
    if (blackoutDates.length === 0 || blackoutDates.includes('none')) {
      return false;
    }
    
    // Normalize date to start of day in Israel time zone
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // Format the date to YYYY-MM-DD for comparison
    const year = normalizedDate.getFullYear();
    const month = (normalizedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = normalizedDate.getDate().toString().padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    // Check if the dateString is in the blackoutDates array
    const isBlackout = blackoutDates.includes(dateString);
    
    if (isBlackout) {
      console.log(`Date ${dateString} is a blackout date`);
    }
    
    return isBlackout;
  }
  
  // Add a method to check if a date is a one-time delivery date
  isOneTimeDate(date) {
    // Get one-time dates from settings
    const oneTimeDates = window.deliverySettings?.oneTimeDates || [];
    
    // If no one-time dates or contains 'none', return false
    if (oneTimeDates.length === 0 || oneTimeDates.includes('none')) {
      return false;
    }
    
    // Normalize date to start of day in Israel time zone
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // Format the date to YYYY-MM-DD for comparison
    const year = normalizedDate.getFullYear();
    const month = (normalizedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = normalizedDate.getDate().toString().padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    // Check if any of the one-time dates match
    for (const oneTimeDate of oneTimeDates) {
      // Skip empty entries
      if (!oneTimeDate || oneTimeDate === 'none') continue;
      
      // Try direct string comparison first
      if (oneTimeDate.trim() === dateString) {
        console.log(`Date ${dateString} is a one-time delivery date (exact match)`);
        return true;
      }
      
      // Parse parts from YYYY-MM-DD format
      const parts = oneTimeDate.trim().split('-');
      if (parts.length === 3) {
        const otYear = parseInt(parts[0], 10);
        const otMonth = parseInt(parts[1], 10);
        const otDay = parseInt(parts[2], 10);
        
        // Compare with normalized date components
        const normalizedYear = normalizedDate.getFullYear();
        const normalizedMonth = normalizedDate.getMonth() + 1; // JavaScript months are 0-based
        const normalizedDay = normalizedDate.getDate();
        
        if (otYear === normalizedYear && otMonth === normalizedMonth && otDay === normalizedDay) {
          console.log(`Date ${dateString} is a one-time delivery date (parts match)`);
          return true;
        }
      }
    }
    
    return false;
  }
};

// Initialize the delivery frequency selector when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing delivery frequency selector');
  
  // Initialize selectors - Fix the selector for cart drawer
  const cartDrawerSelector = document.querySelector('#DrawerDeliveryFrequencySelector');
  const cartPageSelector = document.querySelector('.delivery-frequency-modal');
  
  // Create instances
  let drawerInstance = null;
  let pageInstance = null;
  
  if (cartDrawerSelector) {
    console.log('Found cart drawer selector');
    drawerInstance = new DeliveryFrequencySelector(cartDrawerSelector);
  }
  
  if (cartPageSelector) {
    console.log('Found cart page selector');
    pageInstance = new DeliveryFrequencySelector(cartPageSelector);
  }
  
  // Hide LOYALFAN discount in cart
  function hideLoyalfanDiscount() {
    const discountElements = document.querySelectorAll('.cart__discounts');
    discountElements.forEach(element => {
      if (element.textContent.includes('LOYALFAN')) {
        element.classList.add('hide-loyalfan-discount');
      }
    });
  }
  
  // Run initially
  hideLoyalfanDiscount();
  
  // Handle checkout button clicks
  function handleCheckoutButtonClick() {
    const checkoutButtons = document.querySelectorAll('#CheckOut');
    
    checkoutButtons.forEach(button => {
      // Skip if already handled by this function
      if (button.hasAttribute('data-delivery-handler-attached')) {
        return;
      }
      
      // Mark as handled by this function
      button.setAttribute('data-delivery-handler-attached', 'true');
      
      // Add our click handler without replacing the button
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        // Determine if this is the cart page or drawer
        const isCartPage = window.location.pathname.includes('/cart') || 
                           this.closest('.cart__subtotal-widget') !== null;
        
        // Find the appropriate delivery selector
        let deliverySelector;
        if (isCartPage) {
          // For cart page - use the modal version
          deliverySelector = document.querySelector('#DeliveryFrequencySelector.delivery-frequency-modal');
        } else {
          // For drawer cart - use the slide-up version
          deliverySelector = document.querySelector('#DrawerDeliveryFrequencySelector');
        }
        
        if (deliverySelector) {
          // Show the delivery selector
          deliverySelector.style.display = 'block';
          
          // Show the panel and overlay with animation
          const panel = deliverySelector.querySelector('.delivery-date-panel');
          const overlay = deliverySelector.querySelector('.delivery-panel-overlay');
          
          if (overlay) {
            overlay.style.display = 'block';
          }
          
          // Add a slight delay to ensure smooth animation
          setTimeout(() => {
            if (panel) {
              panel.classList.add('show');
            }
          }, 10);
          
          document.body.style.overflow = 'hidden';
          
          // Find the instance for this selector
          const instance = window.deliveryFrequencyInstances.find(
            inst => inst.selector === deliverySelector
          );
          
          if (instance) {
            // Reset to first step
            instance.currentStep = 1;
            instance.updateUI();
          }
        } else {
          // If no delivery selector is found, proceed to checkout
          document.getElementById('cart').submit();
        }
      });
    });
  }
  
  // Set up checkout buttons
  handleCheckoutButtonClick();
  
  // Re-setup checkout buttons when cart is updated
  document.addEventListener('cart-updated', function() {
    console.log('Cart updated, setting up checkout buttons again');
    setTimeout(() => {
      handleCheckoutButtonClick();
      
      // Also update the delivery info display and check cart comparison
      if (drawerInstance) {
        drawerInstance.displaySavedSelectionsInCart();
        drawerInstance.compareCartWithLastOrder();
      }
      if (pageInstance) {
        pageInstance.displaySavedSelectionsInCart();
        pageInstance.compareCartWithLastOrder();
      }
      
      // Hide LOYALFAN discount when cart is updated
      hideLoyalfanDiscount();
    }, 500);
  });
  
  // Listen for custom event to show delivery selector
  document.addEventListener('show-delivery-selector', function(e) {
    if (e.detail.isDrawer && drawerInstance) {
      drawerInstance.showPanel();
    } else if (pageInstance) {
      pageInstance.showPanel();
    }
  });

  // Add a global event listener to update all delivery frequency selectors when the cart is updated
  document.addEventListener('cart-updated', function() {
    // Update all delivery frequency instances
    if (window.deliveryFrequencyInstances && window.deliveryFrequencyInstances.length > 0) {
      window.deliveryFrequencyInstances.forEach(instance => {
        if (instance && typeof instance.setCartTotal === 'function') {
          instance.setCartTotal();
        }
      });
    }
  });
}); 
 